import React, { useState, useCallback, useRef, useEffect, memo } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
  MarkerType,
  useReactFlow,
  ReactFlowProvider,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Plus, X, ChevronUp, ChevronDown, Download, Upload, GripVertical, Calculator, Variable, StickyNote, Save, Check, AlertCircle, Trash2 } from 'lucide-react';

// Helper function to convert display name to slug
const toSlug = (text) => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
};

// Modern color palette with softer tones
const COLOR_PALETTE = [
  { bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-200', accent: 'bg-sky-100' },
  { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', accent: 'bg-violet-100' },
  { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', accent: 'bg-emerald-100' },
  { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', accent: 'bg-rose-100' },
  { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', accent: 'bg-amber-100' },
  { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200', accent: 'bg-cyan-100' },
  { bg: 'bg-fuchsia-50', text: 'text-fuchsia-700', border: 'border-fuchsia-200', accent: 'bg-fuchsia-100' },
  { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', accent: 'bg-indigo-100' },
];

const getRandomColor = () => COLOR_PALETTE[Math.floor(Math.random() * COLOR_PALETTE.length)];

// Calculation Component
const CalculationComponent = ({ data, onUpdate, allItems, itemIndex, onShowSuggestions }) => {
  const formulaRef = useRef(null);
  
  const handleFormulaChange = (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    onUpdate({ ...data, formula: value });
    
    if (value[cursorPos - 1] === '@') {
      const rect = formulaRef.current.getBoundingClientRect();
      onShowSuggestions(itemIndex, rect, cursorPos, 'formula');
    }
  };

  const renderFormula = () => {
    if (!data.formula) return <span className="text-slate-400 text-sm">Type @ to reference values...</span>;
    
    const parts = [];
    let lastIndex = 0;
    const regex = /@([\w.]+)/g;
    let match;
    
    while ((match = regex.exec(data.formula)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ type: 'text', content: data.formula.substring(lastIndex, match.index) });
      }
      
      const refName = match[1];
      const refItem = allItems.find(item => {
        if (refName.includes('.')) {
          return `${item.nodeSlug}.${item.data.name}` === refName;
        }
        return item.data.name === refName;
      });
      
      if (refItem) {
        const value = refItem.data.result !== undefined ? refItem.data.result : refItem.data.value;
        parts.push({ 
          type: 'ref', 
          content: value, 
          color: refItem.data.color,
          name: refName 
        });
      } else {
        parts.push({ type: 'text', content: match[0] });
      }
      
      lastIndex = match.index + match[0].length;
    }
    
    if (lastIndex < data.formula.length) {
      parts.push({ type: 'text', content: data.formula.substring(lastIndex) });
    }
    
    return parts.map((part, i) => 
      part.type === 'ref' ? (
        <span key={i} className={`${part.color.bg} ${part.color.text} ${part.color.border} px-2 py-0.5 rounded-md border font-medium text-sm inline-flex items-center gap-1`}>
          <span className="text-xs opacity-60">→</span>
          {part.content}
        </span>
      ) : (
        <span key={i} className="text-slate-600">{part.content}</span>
      )
    );
  };

  const colorClasses = data.color || COLOR_PALETTE[0];

  return (
    <div className="group">
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${colorClasses.accent} transition-all`}>
          <Calculator size={18} className={colorClasses.text} />
        </div>
        
        <div className="flex-1 space-y-2">
          <input 
            type="text" 
            placeholder="calculation_name"
            value={data.name || ''}
            onChange={(e) => onUpdate({ ...data, name: e.target.value })}
            className="w-full px-0 py-1 text-base font-semibold border-0 border-b-2 border-transparent hover:border-slate-200 focus:border-slate-400 rounded-none bg-transparent focus:outline-none transition-colors placeholder:text-slate-300"
          />
          <input 
            ref={formulaRef}
            type="text" 
            placeholder="Enter formula (e.g., @var1 * 2 + @var2)"
            value={data.formula || ''}
            onChange={handleFormulaChange}
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-transparent transition-all placeholder:text-slate-400"
          />
          <div className="px-3 py-2 min-h-[32px] bg-slate-50 rounded-lg text-sm">
            {renderFormula()}
          </div>
        </div>
        
        <div className={`${colorClasses.bg} ${colorClasses.border} border rounded-xl px-4 py-3 min-w-[80px] text-center shadow-sm`}>
          <div className="text-xs font-medium text-slate-500 mb-1">Result</div>
          <div className={`text-2xl font-bold ${colorClasses.text}`}>
            {data.result !== null && data.result !== undefined ? data.result : '—'}
          </div>
        </div>
      </div>
    </div>
  );
};

// Variable Component
const VariableComponent = ({ data, onUpdate, allItems, itemIndex, onShowSuggestions }) => {
  const valueRef = useRef(null);
  
  const handleValueChange = (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    onUpdate({ ...data, value: value });
    
    if (value[cursorPos - 1] === '@') {
      const rect = valueRef.current.getBoundingClientRect();
      onShowSuggestions(itemIndex, rect, cursorPos, 'value');
    }
  };

  // Auto-resize textarea
  useEffect(() => {
    if (valueRef.current) {
      valueRef.current.style.height = 'auto';
      valueRef.current.style.height = valueRef.current.scrollHeight + 'px';
    }
  }, [data.value]);

  const colorClasses = data.color || COLOR_PALETTE[0];

  return (
    <div className="group">
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${colorClasses.accent} transition-all`}>
          <Variable size={18} className={colorClasses.text} />
        </div>
        
        <div className="flex-1 space-y-2 min-w-0">
          <input 
            type="text" 
            placeholder="variable_name"
            value={data.name || ''}
            onChange={(e) => onUpdate({ ...data, name: e.target.value })}
            className="w-full px-0 py-1 text-base font-semibold border-0 border-b-2 border-transparent hover:border-slate-200 focus:border-slate-400 rounded-none bg-transparent focus:outline-none transition-colors placeholder:text-slate-300"
          />
          <textarea 
            ref={valueRef}
            placeholder="Enter value or @ to reference"
            value={data.value || ''}
            onChange={handleValueChange}
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-transparent transition-all placeholder:text-slate-400 resize-none overflow-hidden whitespace-pre-wrap"
            rows="1"
            style={{ minHeight: '40px' }}
          />
        </div>
        
        <div className={`${colorClasses.bg} ${colorClasses.border} border rounded-xl px-4 py-3 min-w-[80px] text-center shadow-sm`}>
          <div className="text-xs font-medium text-slate-500 mb-1">Value</div>
          <div className={`text-2xl font-bold ${colorClasses.text} break-all`}>
            {data.result !== null && data.result !== undefined ? data.result : '—'}
          </div>
        </div>
      </div>
    </div>
  );
};

// Note Component
const NoteComponent = ({ data, onUpdate, allItems, itemIndex, onShowSuggestions }) => {
  const contentRef = useRef(null);
  
  const handleContentChange = (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    onUpdate({ ...data, content: value });
    
    if (value[cursorPos - 1] === '@') {
      const rect = contentRef.current.getBoundingClientRect();
      onShowSuggestions(itemIndex, rect, cursorPos, 'content');
    }
  };

  // Auto-resize textarea
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.style.height = 'auto';
      contentRef.current.style.height = contentRef.current.scrollHeight + 'px';
    }
  }, [data.content]);

  const renderContent = () => {
    if (!data.content) return <span className="text-slate-400">Add your notes here...</span>;
    
    const parts = [];
    let lastIndex = 0;
    const regex = /@([\w.]+)/g;
    let match;
    
    while ((match = regex.exec(data.content)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ type: 'text', content: data.content.substring(lastIndex, match.index) });
      }
      
      const refName = match[1];
      const refItem = allItems.find(item => {
        if (refName.includes('.')) {
          return `${item.nodeSlug}.${item.data.name}` === refName;
        }
        return item.data.name === refName;
      });
      
      if (refItem) {
        const value = refItem.data.result !== undefined ? refItem.data.result : refItem.data.value;
        parts.push({ 
          type: 'ref', 
          content: value, 
          color: refItem.data.color,
          name: refName 
        });
      } else {
        parts.push({ type: 'text', content: match[0] });
      }
      
      lastIndex = match.index + match[0].length;
    }
    
    if (lastIndex < data.content.length) {
      parts.push({ type: 'text', content: data.content.substring(lastIndex) });
    }
    
    return parts.map((part, i) => 
      part.type === 'ref' ? (
        <span key={i} className={`${part.color.bg} ${part.color.text} ${part.color.border} px-2 py-0.5 rounded-md border font-medium text-sm inline-flex items-center gap-1`}>
          <span className="text-xs opacity-60">→</span>
          {part.content}
        </span>
      ) : (
        <span key={i} className="break-words">{part.content}</span>
      )
    );
  };

  return (
    <div className="group">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-amber-50 transition-all">
          <StickyNote size={18} className="text-amber-600" />
        </div>
        
        <div className="flex-1 space-y-2 min-w-0">
          <div className="text-sm font-semibold text-slate-700">Note</div>
          <textarea 
            ref={contentRef}
            placeholder="Type @ to reference values in your note..."
            value={data.content || ''}
            onChange={handleContentChange}
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg resize-none bg-white focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-transparent transition-all placeholder:text-slate-400 overflow-hidden whitespace-pre-wrap"
            rows="1"
            style={{ minHeight: '40px' }}
          />
          <div className="px-3 py-2 min-h-[44px] bg-amber-50 rounded-lg text-sm border border-amber-100 break-words whitespace-pre-wrap text-left">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

// Item Container with modern styling
const ItemContainer = memo(({ 
  id, 
  type, 
  data, 
  index,
  nodeId,
  allItems,
  onUpdate, 
  onDelete, 
  onMoveUp,
  onMoveDown,
  onShowSuggestions,
  isFirst,
  isLast
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const showHandle = type === 'calculation' || type === 'variable';
  
  // Get accent color for the border
  const colorClasses = data.color || COLOR_PALETTE[0];
  const accentColor = colorClasses.text.replace('text-', '');

  return (
    <div 
      className="relative mb-3 rounded-xl transition-all hover:shadow-md group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        boxShadow: isHovered ? '0 4px 6px -1px rgb(0 0 0 / 0.05), 0 2px 4px -2px rgb(0 0 0 / 0.05)' : 'none',
        zIndex: 1,
        transform: showHandle ? 'translateX(50px)' : 'none',
      }}
    >
      {/* Main content container with border */}
      <div 
        className="bg-white rounded-xl border border-slate-200 hover:border-slate-300 transition-all overflow-hidden"
        style={{
          borderRight: showHandle ? 'none' : undefined,
          borderTopRightRadius: showHandle ? 0 : undefined,
          borderBottomRightRadius: showHandle ? 0 : undefined,
        }}
      >
        {showHandle && (
          <>
            {/* Extended colored tab on the right */}
            <div 
              className="absolute top-0 bottom-0 w-3 transition-all"
              style={{ 
                right: -3,
                backgroundColor: `var(--${accentColor})`,
                borderTopRightRadius: '0.75rem',
                borderBottomRightRadius: '0.75rem',
              }}
            />
            <Handle
              type="source"
              position={Position.Right}
              id={`${nodeId}-${id}`}
              className="!w-3 !h-3 !bg-gradient-to-br !from-blue-500 !to-indigo-500 !border-2 !border-white !shadow-md"
              style={{ right: -9, zIndex: 10 }}
            />
          </>
        )}

      <div className="absolute left-2 top-1/2 -translate-y-1/2 flex flex-col items-center gap-0.5" style={{ zIndex: 2 }}>
        <button
          onClick={() => onMoveUp(index)}
          disabled={isFirst}
          className={`p-1 rounded-md transition-all ${
            isFirst 
              ? 'text-slate-200 cursor-not-allowed' 
              : 'text-slate-400 hover:text-blue-600 hover:bg-blue-50'
          } ${isHovered || !isFirst ? 'opacity-100' : 'opacity-0'}`}
        >
          <ChevronUp size={14} />
        </button>
        <div className={`p-0.5 rounded ${isHovered ? 'bg-slate-100' : ''}`}>
          <GripVertical size={14} className={`text-slate-300 ${isHovered ? 'text-slate-400' : ''}`} />
        </div>
        <button
          onClick={() => onMoveDown(index)}
          disabled={isLast}
          className={`p-1 rounded-md transition-all ${
            isLast 
              ? 'text-slate-200 cursor-not-allowed' 
              : 'text-slate-400 hover:text-blue-600 hover:bg-blue-50'
          } ${isHovered || !isLast ? 'opacity-100' : 'opacity-0'}`}
        >
          <ChevronDown size={14} />
        </button>
      </div>

      {isHovered && (
        <button
          onClick={() => onDelete(id)}
          className="absolute top-2 right-2 p-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-all shadow-sm"
          style={{ zIndex: 10 }}
        >
          <X size={14} />
        </button>
      )}

      <div className="pl-12 pr-4 py-4">
        {type === 'calculation' ? (
          <CalculationComponent 
            data={data} 
            onUpdate={onUpdate} 
            allItems={allItems}
            itemIndex={index}
            onShowSuggestions={onShowSuggestions}
          />
        ) : type === 'variable' ? (
          <VariableComponent 
            data={data} 
            onUpdate={onUpdate}
            allItems={allItems}
            itemIndex={index}
            onShowSuggestions={onShowSuggestions}
          />
        ) : (
          <NoteComponent 
            data={data} 
            onUpdate={onUpdate}
            allItems={allItems}
            itemIndex={index}
            onShowSuggestions={onShowSuggestions}
          />
        )}
      </div>
      </div>
    </div>
  );
});

ItemContainer.displayName = 'ItemContainer';

// Custom Node Component with paper feel
const CustomCalculatorNode = ({ id, data }) => {
  const [items, setItems] = useState(data.items || []);
  const [displayName, setDisplayName] = useState(data.displayName || 'New Node');
  const [isEditingName, setIsEditingName] = useState(false);
  const [calcCounter, setCalcCounter] = useState(1);
  const [varCounter, setVarCounter] = useState(1);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionPosition, setSuggestionPosition] = useState({ top: 0, left: 0 });
  const [focusedItemIndex, setFocusedItemIndex] = useState(null);
  const [focusedField, setFocusedField] = useState(null);

  const nodeSlug = toSlug(displayName);

  const getAllAvailableItems = useCallback(() => {
    const localItems = items.map(item => ({
      ...item,
      nodeSlug: nodeSlug,
      isLocal: true
    }));

    const connectedItems = (data.connectedItems || []).map(item => ({
      ...item,
      isLocal: false
    }));

    return [...connectedItems, ...localItems];
  }, [items, data.connectedItems, nodeSlug]);

  const evaluateExpression = useCallback((expression, availableItems) => {
    try {
      let processed = expression;
      
      const regex = /@([\w.]+)/g;
      let match;
      while ((match = regex.exec(expression)) !== null) {
        const refName = match[1];
        const refItem = availableItems.find(item => {
          if (refName.includes('.')) {
            return `${item.nodeSlug}.${item.data.name}` === refName;
          }
          return item.data.name === refName;
        });
        if (refItem) {
          const value = refItem.data.result !== undefined ? refItem.data.result : refItem.data.value;
          processed = processed.replace(match[0], value);
        }
      }
      
      const result = Function('"use strict"; return (' + processed + ')')();
      return { value: parseFloat(result.toFixed(8)), error: null };
    } catch (err) {
      return { value: null, error: 'Invalid' };
    }
  }, []);

  useEffect(() => {
    const allAvailable = getAllAvailableItems();
    
    const recalculated = items.map((item, index) => {
      const localItemsUpToIndex = allAvailable.filter(i => {
        if (i.isLocal) {
          const localIndex = items.findIndex(li => li.id === i.id);
          return localIndex < index;
        }
        return true;
      });
      
      if (item.type === 'calculation' && item.data.formula) {
        const { value } = evaluateExpression(item.data.formula, localItemsUpToIndex);
        return {
          ...item,
          data: { ...item.data, result: value }
        };
      } else if (item.type === 'variable' && item.data.value) {
        if (item.data.value.includes('@')) {
          const { value } = evaluateExpression(item.data.value, localItemsUpToIndex);
          return {
            ...item,
            data: { ...item.data, result: value }
          };
        } else {
          const numValue = parseFloat(item.data.value);
          return {
            ...item,
            data: { ...item.data, result: isNaN(numValue) ? item.data.value : numValue }
          };
        }
      }
      
      return item;
    });
    
    const hasChanges = recalculated.some((item, index) => 
      item.data.result !== items[index].data.result
    );
    
    if (hasChanges) {
      setItems(recalculated);
    }
  }, [items, data.connectedItems, evaluateExpression, getAllAvailableItems]);
  
  const handleAddCalculation = useCallback(() => {
    const newItem = {
      id: crypto.randomUUID(),
      type: 'calculation',
      data: { 
        name: `calc_${calcCounter}`,
        formula: '',
        result: null,
        color: getRandomColor()
      }
    };
    setItems(prev => [...prev, newItem]);
    setCalcCounter(prev => prev + 1);
  }, [calcCounter]);
  
  const handleAddVariable = useCallback(() => {
    const newItem = {
      id: crypto.randomUUID(),
      type: 'variable',
      data: { 
        name: `var_${varCounter}`,
        value: '',
        result: null,
        color: getRandomColor()
      }
    };
    setItems(prev => [...prev, newItem]);
    setVarCounter(prev => prev + 1);
  }, [varCounter]);
  
  const handleAddNote = useCallback(() => {
    const newItem = {
      id: crypto.randomUUID(),
      type: 'note',
      data: { content: '' }
    };
    setItems(prev => [...prev, newItem]);
  }, []);
  
  const handleUpdateItem = useCallback((itemId, newData) => {
    setItems(prev => {
      // If updating the name, check for uniqueness
      if (newData.name !== undefined) {
        const itemBeingUpdated = prev.find(item => item.id === itemId);
        
        // Only check uniqueness for calculations and variables
        if (itemBeingUpdated && (itemBeingUpdated.type === 'calculation' || itemBeingUpdated.type === 'variable')) {
          const nameExists = prev.some(item => 
            item.id !== itemId && 
            (item.type === 'calculation' || item.type === 'variable') &&
            item.data.name === newData.name
          );
          
          if (nameExists) {
            // Don't update if name already exists
            return prev;
          }
        }
      }
      
      return prev.map(item => 
        item.id === itemId 
          ? { ...item, data: { ...item.data, ...newData } }
          : item
      );
    });
  }, []);
  
  const handleDeleteItem = useCallback((itemId) => {
    setItems(prev => prev.filter(item => item.id !== itemId));
  }, []);
  
  const handleMoveUp = useCallback((index) => {
    if (index === 0) return;
    setItems(prev => {
      const newItems = [...prev];
      [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
      return newItems;
    });
  }, []);

  const handleMoveDown = useCallback((index) => {
    setItems(prev => {
      if (index >= prev.length - 1) return prev;
      const newItems = [...prev];
      [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
      return newItems;
    });
  }, []);

  const handleShowSuggestions = useCallback((itemIndex, rect, cursorPos, field) => {
    const allAvailable = getAllAvailableItems();
    const availableForItem = allAvailable.filter(item => {
      if (item.type !== 'calculation' && item.type !== 'variable') return false;
      
      if (item.isLocal) {
        const localIndex = items.findIndex(li => li.id === item.id);
        return localIndex < itemIndex;
      }
      return true;
    });
    
    if (availableForItem.length === 0) {
      setShowSuggestions(false);
      return;
    }
    
    // Position to the right of the entire item container
    setSuggestionPosition({
      top: rect.top + window.scrollY,
      left: rect.right + window.scrollX + 10
    });
    setFocusedItemIndex(itemIndex);
    setFocusedField(field);
    setShowSuggestions(true);
  }, [items, getAllAvailableItems]);

  const insertReference = useCallback((refName) => {
    if (focusedItemIndex === null) return;
    
    const item = items[focusedItemIndex];
    const fieldName = focusedField;
    let currentValue = '';
    
    if (fieldName === 'formula') {
      currentValue = item.data.formula || '';
    } else if (fieldName === 'value') {
      currentValue = item.data.value || '';
    } else if (fieldName === 'content') {
      currentValue = item.data.content || '';
    }
    
    const lastAtIndex = currentValue.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      const newValue = currentValue.substring(0, lastAtIndex) + '@' + refName + currentValue.substring(lastAtIndex + 1);
      handleUpdateItem(item.id, { [fieldName]: newValue });
    }
    
    setShowSuggestions(false);
  }, [focusedItemIndex, focusedField, items, handleUpdateItem]);

  useEffect(() => {
    const handleClickOutside = () => setShowSuggestions(false);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    if (data.onItemsChange) {
      data.onItemsChange(id, items);
    }
  }, [items, id, data]);

  const allAvailableItems = getAllAvailableItems();

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-slate-200 min-w-[550px] max-w-[650px] overflow-visible" style={{
      boxShadow: '0 10px 40px -10px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.02)'
    }}>
      <Handle
        type="target"
        position={Position.Left}
        className="!w-3 !h-3 !bg-gradient-to-br !from-emerald-500 !to-green-500 !border-2 !border-white !shadow-md"
        style={{ left: -6 }}
      />

      <div className="p-6">
        <div className="mb-6 pb-4 border-b border-slate-100">
          {isEditingName ? (
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              onBlur={() => setIsEditingName(false)}
              onKeyDown={(e) => e.key === 'Enter' && setIsEditingName(false)}
              className="text-2xl font-bold border-0 border-b-2 border-blue-500 outline-none w-full bg-transparent"
              autoFocus
            />
          ) : (
            <h2 
              onClick={() => setIsEditingName(true)}
              className="text-2xl font-bold cursor-pointer hover:text-blue-600 transition-colors"
            >
              {displayName}
            </h2>
          )}
          <div className="text-xs text-slate-400 font-mono mt-2 flex items-center gap-2">
            <span className="px-2 py-0.5 bg-slate-50 rounded-md border border-slate-200">{nodeSlug}</span>
          </div>
        </div>
        
        <div className="mb-5 overflow-visible pr-1 space-y-0" style={{
          scrollbarWidth: 'thin',
          scrollbarColor: '#cbd5e1 transparent'
        }}>
          {items.map((item, index) => (
            <ItemContainer
              key={item.id}
              id={item.id}
              type={item.type}
              data={item.data}
              index={index}
              nodeId={id}
              allItems={allAvailableItems.filter(i => {
                if (i.isLocal) {
                  const localIndex = items.findIndex(li => li.id === i.id);
                  return localIndex < index;
                }
                return true;
              })}
              onUpdate={(newData) => handleUpdateItem(item.id, newData)}
              onDelete={handleDeleteItem}
              onMoveUp={handleMoveUp}
              onMoveDown={handleMoveDown}
              onShowSuggestions={handleShowSuggestions}
              isFirst={index === 0}
              isLast={index === items.length - 1}
            />
          ))}
        </div>
        
        <div className="flex gap-2 pt-2">
          <button
            onClick={handleAddCalculation}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl hover:from-blue-600 hover:to-indigo-600 transition-all shadow-sm hover:shadow-md font-medium"
          >
            <Calculator size={16} />
            Calculation
          </button>
          <button
            onClick={handleAddVariable}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-xl hover:from-emerald-600 hover:to-green-600 transition-all shadow-sm hover:shadow-md font-medium"
          >
            <Variable size={16} />
            Variable
          </button>
          <button
            onClick={handleAddNote}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-amber-400 to-orange-400 text-white rounded-xl hover:from-amber-500 hover:to-orange-500 transition-all shadow-sm hover:shadow-md font-medium"
          >
            <StickyNote size={16} />
            Note
          </button>
        </div>
      </div>

      {showSuggestions && focusedItemIndex !== null && (
        <div
          className="fixed bg-white rounded-xl shadow-2xl border border-slate-200 py-2 z-50 min-w-[280px] max-h-96 overflow-y-auto"
          style={{ 
            top: suggestionPosition.top, 
            left: suggestionPosition.left,
            boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {allAvailableItems.filter(item => 
            item.isLocal && 
            (item.type === 'calculation' || item.type === 'variable') &&
            items.findIndex(li => li.id === item.id) < focusedItemIndex
          ).length > 0 && (
            <>
              <div className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">Local Variables</div>
              {allAvailableItems.filter(item => 
                item.isLocal && 
                (item.type === 'calculation' || item.type === 'variable') &&
                items.findIndex(li => li.id === item.id) < focusedItemIndex
              ).map((item) => (
                <button
                  key={item.id}
                  onClick={() => insertReference(item.data.name)}
                  className="w-full px-4 py-2.5 hover:bg-slate-50 flex items-center justify-between gap-3 transition-colors group"
                >
                  <span className={`${item.data.color.bg} ${item.data.color.text} ${item.data.color.border} px-3 py-1.5 rounded-lg border font-semibold text-sm shadow-sm group-hover:shadow`}>
                    {item.data.result !== null && item.data.result !== undefined ? item.data.result : '—'}
                  </span>
                  <span className="text-slate-600 text-sm font-mono">@{item.data.name}</span>
                </button>
              ))}
            </>
          )}

          {Object.entries(
            allAvailableItems
              .filter(item => !item.isLocal && (item.type === 'calculation' || item.type === 'variable'))
              .reduce((acc, item) => {
                if (!acc[item.nodeSlug]) acc[item.nodeSlug] = [];
                acc[item.nodeSlug].push(item);
                return acc;
              }, {})
          ).map(([nodeSlug, nodeItems]) => (
            <div key={nodeSlug}>
              <div className="px-4 py-2 text-xs font-semibold text-blue-600 uppercase tracking-wide mt-2 bg-blue-50/50">
                From: {nodeSlug}
              </div>
              {nodeItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => insertReference(`${nodeSlug}.${item.data.name}`)}
                  className="w-full px-4 py-2.5 hover:bg-slate-50 flex items-center justify-between gap-3 transition-colors group"
                >
                  <span className={`${item.data.color.bg} ${item.data.color.text} ${item.data.color.border} px-3 py-1.5 rounded-lg border font-semibold text-sm shadow-sm group-hover:shadow`}>
                    {item.data.result !== null && item.data.result !== undefined ? item.data.result : '—'}
                  </span>
                  <span className="text-slate-600 text-sm font-mono">@{nodeSlug}.{item.data.name}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const nodeTypes = {
  calculator: CustomCalculatorNode,
};

// WorkflowSaver Component
const WorkflowSaver = ({
  workflowData,
  storageKey = 'calculator-flow-data',
  onSaveSuccess,
  onSaveError,
  autoSave = false,
  autoSaveDelay = 2000,
}) => {
  const [saveStatus, setSaveStatus] = useState('idle');
  const autoSaveTimerRef = useRef(null);

const saveWorkflow = async () => {
  try {
    setSaveStatus('saving');
    
    const dataToSave = {
      nodes: workflowData.nodes.map(node => ({
        id: node.id,
        type: node.type,
        position: node.position,
        data: {
          displayName: node.data.displayName,
          items: node.data.items
        }
      })),
      edges: workflowData.edges,
      nodeId: workflowData.nodeId,
      timestamp: new Date().toISOString()
    };

    // Use localStorage for browser compatibility
    localStorage.setItem(storageKey, JSON.stringify(dataToSave));
    
    setSaveStatus('success');
    if (onSaveSuccess) onSaveSuccess(dataToSave);
    setTimeout(() => setSaveStatus('idle'), 2000);
  } catch (error) {
    console.error('Error saving workflow:', error);
    setSaveStatus('error');
    if (onSaveError) onSaveError(error);
    setTimeout(() => setSaveStatus('idle'), 3000);
  }
};

  useEffect(() => {
    if (!autoSave || !workflowData) return;
    
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = setTimeout(() => {
      saveWorkflow();
    }, autoSaveDelay);

    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [workflowData, autoSave, autoSaveDelay]);

  return (
    <button
      onClick={saveWorkflow}
      disabled={saveStatus === 'saving'}
      className={`
        flex items-center gap-2 px-5 py-3 rounded-xl font-medium
        transition-all shadow-sm hover:shadow-md
        ${saveStatus === 'saving' 
          ? 'bg-slate-100 text-slate-400 cursor-wait' 
          : saveStatus === 'success'
          ? 'bg-green-50 text-green-700 border border-green-200'
          : saveStatus === 'error'
          ? 'bg-red-50 text-red-700 border border-red-200'
          : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
        }
      `}
    >
      {saveStatus === 'saving' && (
        <>
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-slate-400 border-t-transparent" />
          Saving...
        </>
      )}
      {saveStatus === 'success' && (
        <>
          <Check size={18} />
          Saved!
        </>
      )}
      {saveStatus === 'error' && (
        <>
          <AlertCircle size={18} />
          Error
        </>
      )}
      {saveStatus === 'idle' && (
        <>
          <Save size={18} />
          Save
        </>
      )}
    </button>
  );
};

function FlowContent() {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [nodeId, setNodeId] = useState(1);
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const { getNode, getEdges } = useReactFlow();
  const fileInputRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);

useEffect(() => {
  const loadData = async () => {
    try {
      const savedData = localStorage.getItem('calculator-flow-data');
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        
        // Restore nodes with onItemsChange callback
        const restoredNodes = parsedData.nodes.map(node => ({
          ...node,
          data: {
            ...node.data,
            connectedItems: [],
            onItemsChange: (nodeId, items) => {
              setNodes((nds) =>
                nds.map((n) =>
                  n.id === nodeId
                    ? { ...n, data: { ...n.data, items } }
                    : n
                )
              );
            }
          }
        }));

        setNodes(restoredNodes);
        setEdges(parsedData.edges || []);
        setNodeId(parsedData.nodeId || restoredNodes.length + 1);
      }
    } catch (error) {
      console.log('No saved data found or error loading:', error);
    } finally {
      setIsLoaded(true);
    }
  };
  
  loadData();
}, [setNodes, setEdges]);


  // Save to storage whenever nodes or edges change (but only after initial load)
useEffect(() => {
  if (!isLoaded) return;
  
  const saveData = () => {
    try {
      const dataToSave = {
        nodes: nodes.map(node => ({
          id: node.id,
          type: node.type,
          position: node.position,
          data: {
            displayName: node.data.displayName,
            items: node.data.items
          }
        })),
        edges: edges,
        nodeId: nodeId,
        timestamp: new Date().toISOString()
      };
      
      localStorage.setItem('calculator-flow-data', JSON.stringify(dataToSave));
    } catch (error) {
      console.error('Error saving to storage:', error);
    }
  };
  
  saveData();
}, [nodes, edges, nodeId, isLoaded]);

  const getConnectedItemsForNode = useCallback((targetNodeId) => {
    const allEdges = getEdges();
    const connectedItems = [];

    allEdges.forEach(edge => {
      if (edge.target === targetNodeId) {
        const sourceNodeId = edge.source;
        const sourceHandleId = edge.sourceHandle;
        
        const sourceNode = getNode(sourceNodeId);
        if (!sourceNode) return;

        const sourceNodeSlug = toSlug(sourceNode.data.displayName);
        const sourceItems = sourceNode.data.items || [];

        const itemId = sourceHandleId.replace(`${sourceNodeId}-`, '');
        const connectedItem = sourceItems.find(item => item.id === itemId);

        if (connectedItem && (connectedItem.type === 'calculation' || connectedItem.type === 'variable')) {
          connectedItems.push({
            ...connectedItem,
            nodeSlug: sourceNodeSlug,
            sourceNodeId: sourceNodeId
          });
        }
      }
    });

    return connectedItems;
  }, [getEdges, getNode]);

  useEffect(() => {
    setNodes((nds) => {
      const nodesWithConnections = nds.map((node) => ({
        ...node,
        data: {
          ...node.data,
          connectedItems: getConnectedItemsForNode(node.id)
        }
      }));
      
      return nodesWithConnections;
    });
  }, [edges, nodes, setNodes, getConnectedItemsForNode]);

  const onConnect = useCallback(
    (params) => {
      if (params.source === params.target) {
        return;
      }

      if (!params.sourceHandle || params.targetHandle) {
        return;
      }
      
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            type: 'smoothstep',
            animated: true,
            style: { stroke: '#94a3b8', strokeWidth: 2 },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: '#94a3b8',
            },
          },
          eds
        )
      );
    },
    [setEdges]
  );

  const addNode = useCallback(() => {
    if (!reactFlowInstance) return;

    const { x, y, zoom } = reactFlowInstance.getViewport();
    const bounds = reactFlowWrapper.current.getBoundingClientRect();
    
    const centerX = (bounds.width / 2 - x) / zoom;
    const centerY = (bounds.height / 2 - y) / zoom;

    const newNode = {
      id: `${nodeId}`,
      type: 'calculator',
      position: { x: centerX - 275, y: centerY - 150 },
      data: { 
        displayName: `Node ${nodeId}`,
        items: [],
        connectedItems: [],
        onItemsChange: (nodeId, items) => {
          setNodes((nds) =>
            nds.map((node) =>
              node.id === nodeId
                ? { ...node, data: { ...node.data, items } }
                : node
            )
          );
        }
      },
    };

    setNodes((nds) => [...nds, newNode]);
    setNodeId((id) => id + 1);
  }, [nodeId, reactFlowInstance, setNodes]);

  const handleExport = useCallback(() => {
    const exportData = {
      nodes: nodes.map(node => ({
        id: node.id,
        type: node.type,
        position: node.position,
        data: {
          displayName: node.data.displayName,
          items: node.data.items
        }
      })),
      edges: edges,
      nodeId: nodeId,
      timestamp: new Date().toISOString()
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `flow-calculator-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [nodes, edges, nodeId]);

  const handleImport = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importData = JSON.parse(e.target.result);
        
        if (!importData.nodes || !Array.isArray(importData.nodes)) {
          alert('Invalid file format');
          return;
        }

        const restoredNodes = importData.nodes.map(node => ({
          ...node,
          data: {
            ...node.data,
            connectedItems: [],
            onItemsChange: (nodeId, items) => {
              setNodes((nds) =>
                nds.map((n) =>
                  n.id === nodeId
                    ? { ...n, data: { ...n.data, items } }
                    : n
                )
              );
            }
          }
        }));

        setNodes(restoredNodes);
        setEdges(importData.edges || []);
        setNodeId(importData.nodeId || restoredNodes.length + 1);

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        console.error('Error importing:', error);
        alert('Error importing file');
      }
    };
    reader.readAsText(file);
  }, [setNodes, setEdges]);

  const handleImportClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleClearMemory = useCallback(() => {
  if (window.confirm('Are you sure you want to clear all saved data? This cannot be undone.')) {
    try {
      localStorage.removeItem('calculator-flow-data');
      
      // Also clear the current workflow
      setNodes([]);
      setEdges([]);
      setNodeId(1);
      
      alert('Memory cleared successfully!');
    } catch (error) {
      console.error('Error clearing memory:', error);
      alert('Error clearing memory');
    }
  }
}, [setNodes, setEdges]);

  return (
    <div className="w-full h-screen flex flex-col" style={{ 
      background: 'linear-gradient(to bottom, #f8fafc 0%, #f1f5f9 100%)'
    }}>
      <div ref={reactFlowWrapper} className="flex-1">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onInit={setReactFlowInstance}
          nodeTypes={nodeTypes}
          fitView
          minZoom={0.1}
          maxZoom={2}
          defaultEdgeOptions={{
            type: 'smoothstep',
            animated: true,
            style: { stroke: '#94a3b8', strokeWidth: 2 },
          }}
        >
          <Background 
            color="#cbd5e1" 
            gap={20} 
            size={1}
            style={{ opacity: 0.4 }}
          />
          <Controls 
            className="!bg-white !border-slate-200 !shadow-lg rounded-xl"
            style={{ 
              boxShadow: '0 10px 40px -10px rgba(0, 0, 0, 0.1)'
            }}
            showInteractive={false}
          />
          <MiniMap
            nodeColor={(node) => '#6366f1'}
            maskColor="rgba(248, 250, 252, 0.8)"
            className="!bg-white !border-slate-200 rounded-xl !shadow-lg"
          />
        </ReactFlow>
      </div>

      <div className="bg-white/80 backdrop-blur-md border-t border-slate-200/50 shadow-2xl">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={addNode}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-xl hover:from-indigo-700 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl font-medium"
            >
              <Plus size={20} strokeWidth={2.5} />
              Add Node
            </button>
            
            <div className="h-10 w-px bg-slate-200"></div>

            <WorkflowSaver
              workflowData={{ nodes, edges, nodeId }}
              autoSave={true}
              autoSaveDelay={2000}
              onSaveSuccess={() => console.log('Auto-saved')}
            />

            <button
              onClick={handleExport}
              disabled={nodes.length === 0}
              className="flex items-center gap-2 px-5 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              <Download size={18} />
              Export
            </button>
            
            <button
              onClick={handleImportClick}
              className="flex items-center gap-2 px-5 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-all shadow-sm hover:shadow-md font-medium"
            >
              <Upload size={18} />
              Import
            </button>
            
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleImport}
              className="hidden"
            />

            <button
              onClick={handleClearMemory}
              className="flex items-center gap-2 px-5 py-3 bg-white border border-red-200 text-red-600 rounded-xl hover:bg-red-50 transition-all shadow-sm hover:shadow-md font-medium"
            >
              <Trash2 size={18} />
              Clear Memory
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

export default function FlowApp() {
  return (
    <ReactFlowProvider>
      <style>{`
        :root {
          --sky-700: #0369a1;
          --violet-700: #6d28d9;
          --emerald-700: #047857;
          --rose-700: #be123c;
          --amber-700: #b45309;
          --cyan-700: #0e7490;
          --fuchsia-700: #a21caf;
          --indigo-700: #4338ca;
        }
      `}</style>
      <FlowContent />
    </ReactFlowProvider>
  );
}